import isNativeApp from "../utils/isNativeApp";

/**
 * capacitorInit — one-time native bootstrap, called once from index.js before
 * React renders. Completely inert on web: isNativeApp() short-circuits and no
 * @capacitor/* module is ever imported into the production web bundle (every
 * import below is dynamic and lives inside the native-only branch).
 *
 * It owns the native concerns that must run at app boot, not inside a
 * login-gated component:
 *   - StatusBar: solid strip (no notch/Dynamic-Island overlap of the toolbar)
 *   - SplashScreen: hide only once the web content has actually painted
 *     (config sets launchAutoHide:false), with a hard ceiling so a failed boot
 *     can never strand the user on the splash — armed before any import runs
 *   - Keyboard: hide the iOS accessory bar; keep the focused input visible
 *   - Android hardware back button: dismiss overlays / navigate SPA history /
 *     double-press-to-exit instead of slamming the app shut
 *   - appStateChange: re-show the splash if the WebView was purged under
 *     memory pressure (the classic WKWebView white-screen-on-resume), always
 *     paired with a guaranteed hide
 *   - push notification taps: a single global listener that buffers tapped
 *     payloads so a cold-start deep link survives until the router and auth are
 *     ready (see onPushAction below)
 */

// --- push-tap pub/sub (cold-start deep-link survival, audit #11) -------------
// Taps that launch the app from a killed state fire before any login-gated hook
// is mounted. We buffer them here and deliver ONLY THE NEWEST buffered tap
// (last-wins) to whoever subscribes (usePushNotifications, post-login):
// replaying every buffered tap pushed ghost history entries and fetched
// tickets the user never viewed. Single-subscriber by contract — do NOT add
// a second consumer expecting a full replay.
let pendingPushActions = [];
let pushActionHandler = null;

/**
 * Subscribe to push-notification taps. If taps were buffered before
 * subscription (cold start), only the NEWEST one is delivered (last-wins).
 * Returns an unsubscribe fn.
 */
export function onPushAction(handler) {
  pushActionHandler = handler;
  if (pendingPushActions.length) {
    // Deliver ONLY the newest buffered tap (last-wins). Replaying every
    // buffered tap pushed ghost history entries and mounted intermediate
    // Ticket pages (each firing a /tickets/u/:id fetch) for chats the
    // user never actually looked at.
    const last = pendingPushActions[pendingPushActions.length - 1];
    pendingPushActions = [];
    try {
      handler(last);
    } catch (_e) {
      /* ignore */
    }
  }
  return () => {
    if (pushActionHandler === handler) pushActionHandler = null;
  };
}

function dispatchPushAction(data) {
  if (!data) return;
  if (pushActionHandler) {
    try {
      pushActionHandler(data);
    } catch (_e) {
      /* ignore */
    }
  } else {
    pendingPushActions.push(data);
  }
}

let initialised = false;

export async function initCapacitor() {
  if (!isNativeApp() || initialised) return;
  initialised = true;

  // Guaranteed splash-hide ceiling, armed BEFORE any plugin import can fail.
  // config sets launchAutoHide:false, so JS owns the hide; if the import below
  // rejects (corrupt chunk, unresolvable bridge, remote fetch failure) this
  // independent timer still hides the splash so the user is never stranded.
  const safetyCeiling = setTimeout(() => {
    import("@capacitor/splash-screen")
      .then(({ SplashScreen }) => SplashScreen.hide())
      .catch(() => {});
  }, 6000);

  let App, SplashScreen, StatusBar, StatusBarStyle, Keyboard, PushNotifications;
  try {
    const [appM, splashM, statusM, keyboardM, pushM] = await Promise.all([
      import("@capacitor/app"),
      import("@capacitor/splash-screen"),
      import("@capacitor/status-bar"),
      import("@capacitor/keyboard"),
      import("@capacitor/push-notifications"),
    ]);
    App = appM.App;
    SplashScreen = splashM.SplashScreen;
    StatusBar = statusM.StatusBar;
    StatusBarStyle = statusM.Style;
    Keyboard = keyboardM.Keyboard;
    PushNotifications = pushM.PushNotifications;
  } catch (err) {
    // Plugins unresolvable — the safetyCeiling above still hides the splash.
    return;
  }

  // Hide the splash once the web content has painted, with a hard ceiling so a
  // never-painting webview still resolves. Reused at boot AND after a resume
  // re-show, so SplashScreen.show() is never left without a paired hide.
  const hideSplashWhenPainted = (maxWaitMs = 6000) => {
    const start = Date.now();
    const tick = () => {
      const root = document.getElementById("root");
      if ((root && root.childElementCount > 0) || Date.now() - start > maxWaitMs) {
        SplashScreen.hide().catch(() => {});
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // --- StatusBar (single-owner model) ---------------------------------------
  // overlay:false on BOTH platforms — the OS insets the WebView below the status
  // strip, so the toolbar needs NO notch math (no top env(safe-area-inset-top)
  // padding; see the toolbar style, which deliberately omits it). This is the
  // ONE chosen story; the earlier overlay:true "full-bleed green under the
  // notch" iteration was reverted because it required env-padding that fought
  // the layout and produced the "two-story" bar. Style.Dark = light glyphs.
  // If a full-bleed look is ever wanted again, flip overlay:true AND re-add the
  // toolbar top env-padding together — never one without the other.
  try {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: StatusBarStyle.Dark });
    // Android status-bar tint (iOS ignores backgroundColor; under overlay the
    // bar is transparent and the green AppBar shows through).
    await StatusBar.setBackgroundColor({ color: "#131614" });
  } catch (_e) {
    /* StatusBar unsupported on this platform — ignore */
  }

  // --- Keyboard: hide iOS accessory bar; keep focused input visible (#6/#9) -
  // config sets resize:'native' which already keeps inputs in the viewport;
  // this is a belt-and-braces scroll for the bottom composer on devices where
  // native resize lands late. Listeners are app-lifetime singletons (guarded
  // by `initialised`), so no teardown needed.
  try {
    await Keyboard.setAccessoryBarVisible({ isVisible: false });
    Keyboard.addListener("keyboardWillShow", () => {
      // Android's resize:"native" already scrolls the focused input into view;
      // re-scrolling here yo-yos the position. iOS only.
      if (window.Capacitor?.getPlatform?.() === "android") return;
      const el = document.activeElement;
      if (el && typeof el.scrollIntoView === "function") {
        // Defer past the native resize so we scroll to the final position.
        setTimeout(() => {
          try {
            el.scrollIntoView({ block: "center", behavior: "auto" });
          } catch (_e) {
            /* ignore */
          }
        }, 50);
      }
    });
  } catch (_e) {
    /* Keyboard plugin unsupported — ignore */
  }

  // --- Android hardware back button (audit #5/#16) --------------------------
  // Order: 1) try to close a TEMPORARY MUI overlay, 2) walk SPA history,
  // 3) at the root, require a second press within 2s before exiting.
  //
  // Selector matches only modal/temporary overlays: Dialog, Popover, and the
  // *temporary* Drawer (its Modal wrapper carries .MuiDrawer-modal). A
  // persistent/docked Drawer (e.g. ContactDrawer, variant="persistent") renders
  // no Modal, so it is excluded — it's layout, not a dismissable overlay.
  //
  // Never-trap guarantee is by NODE IDENTITY, not timing: we attempt to close a
  // given overlay AT MOST ONCE (best-effort Escape on the node + backdrop
  // click — MUI v4's close is a React onKeyDown/onClick and may not fire from a
  // synthetic event on a body-portaled node under React 17's #root delegation).
  // If the SAME overlay node is still present on the next press, the close
  // didn't take, so we fall through to history/exit instead of re-attempting
  // forever. Verify the close itself on a device.
  try {
    let lastBackPress = 0;
    let overlayCloseAttempted = null; // the overlay node we last tried to close
    App.addListener("backButton", ({ canGoBack }) => {
      const overlay = document.querySelector(
        ".MuiDialog-container, .MuiPopover-paper, .MuiDrawer-modal"
      );
      if (overlay && overlay !== overlayCloseAttempted) {
        overlayCloseAttempted = overlay;
        const target =
          overlay.querySelector(
            "input, button, textarea, [tabindex], [role='dialog']"
          ) || overlay;
        try {
          target.dispatchEvent(
            new KeyboardEvent("keydown", {
              key: "Escape",
              code: "Escape",
              keyCode: 27,
              which: 27,
              bubbles: true,
            })
          );
        } catch (_e) {
          /* ignore */
        }
        const backdrop = document.querySelector(".MuiBackdrop-root");
        if (backdrop) backdrop.click();
        return;
      }
      // No temporary overlay, or our one close attempt didn't take → never trap.
      overlayCloseAttempted = null;
      if (canGoBack && window.history.length > 1) {
        window.history.back();
        return;
      }
      const now = Date.now();
      if (now - lastBackPress < 2000) {
        App.exitApp();
      } else {
        lastBackPress = now;
      }
    });
  } catch (_e) {
    /* App plugin unsupported — ignore */
  }

  // --- WKWebView white-screen on resume (audit #2) --------------------------
  // If the WebView content was purged under memory pressure the root is empty
  // on resume; Capacitor reloads it, so we re-show the splash to mask the gap —
  // ALWAYS paired with hideSplashWhenPainted so it can never strand.
  try {
    App.addListener("appStateChange", async ({ isActive }) => {
      if (!isActive) return;
      const root = document.getElementById("root");
      if (root && root.childElementCount === 0) {
        try {
          await SplashScreen.show({ autoHide: false });
        } catch (_e) {
          /* ignore */
        }
        hideSplashWhenPainted(8000);
      }
    });
  } catch (_e) {
    /* ignore */
  }

  // --- Global push-tap listener (audit #11) ---------------------------------
  // Registered at boot so a tap that cold-starts the app is captured. We only
  // BUFFER here; navigation happens in usePushNotifications via onPushAction
  // (it owns react-router history). requestPermissions()/register() stay in the
  // hook, behind the post-login gate (Apple 2.1).
  try {
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      dispatchPushAction(action?.notification?.data || null);
    });
  } catch (_e) {
    /* push plugin unsupported — ignore */
  }

  // --- Splash: hide once the app has painted (audit #7) ---------------------
  // We own the hide (launchAutoHide:false). Replace the import-failure ceiling
  // with the painted-based hide (which has its own ceiling).
  clearTimeout(safetyCeiling);
  hideSplashWhenPainted(6000);
}

export default initCapacitor;
